// ec-appliances.js — Step 2: the appliance picker, the grouped line list, and
// the live meter.
//
// Run 6: the old one-tile-per-appliance grid is gone. This is now a PICKER
// (search + category chips) over a GROUPED DENSE LIST: each added appliance
// is a group (icon, name, per-group units subtotal, remove-all) that can
// hold more than one LINE — an independent watts x qty x hours contributor.
// "+ different wattage" appends another line to a group, which is how a
// user enters e.g. three ceiling fans at 75W / 65W / 150W.
//
// DATA MODEL: EC_WIZARD.state.appliances stays the flat array
// assets/ec-core.js already expects — {key, qty, hours, watts, ...} — with
// one addition: every entry now carries a session-local `id` so a line can
// be targeted even when another line shares its `key`. ec-core.js's
// totalUnits()/applianceUnits() already sum an arbitrary list of selections
// regardless of duplicate keys, so nothing in the math layer changes for
// Phase 1 — the running total is correct for multi-line appliances the
// moment the data model allows them. (The per-appliance BREAKDOWN used by
// the Step 3 mythbuster does not yet merge same-key lines back into one row
// — that aggregation is Phase 2, gated separately; see PROGRESS.md.)
//
// Renders category tabs/chips and the search index from
// EC_RATES.appliances, so adding an appliance to config is the only step
// needed to make it findable here. Keeps EC_WIZARD.state.appliances in
// sync, and drives the meter bar: running total, count-up, slab ladder,
// pulse rate and the slab-crossing alert — this half is unchanged from
// the previous run.

(function (root) {
  'use strict';

  // Labels come from the locale files (step2.category.<key>) via i18nCat()
  // below, not from this list — it only fixes the tab order and which
  // config `group` values map to a tab.
  var CATEGORIES = [
    { key: 'cooling', i18nKey: 'step2.category.cooling' },
    { key: 'lighting', i18nKey: 'step2.category.lighting' },
    { key: 'kitchen', i18nKey: 'step2.category.kitchen' },
    { key: 'entertainment', i18nKey: 'step2.category.entertainment' },
    { key: 'water', i18nKey: 'step2.category.water' },
    { key: 'personal', i18nKey: 'step2.category.personal' },
    { key: 'other', i18nKey: 'step2.category.other' }
  ];

  // Schematic line icons, drawn to the same 24x24 grid with a 1.6 stroke so the
  // set reads as one family. Inline to avoid a sprite request for ~1KB of paths.
  var ICONS = {
    fan:
      '<circle cx="12" cy="12" r="1.7"/>' +
      '<path d="M12 10.3c0-3.2.9-5.1 2.7-5.1 1.6 0 2.3 1.8 1.2 3.2-.9 1.2-2.3 1.8-3.9 1.9z"/>' +
      '<path d="M12 10.3c0-3.2.9-5.1 2.7-5.1 1.6 0 2.3 1.8 1.2 3.2-.9 1.2-2.3 1.8-3.9 1.9z" transform="rotate(120 12 12)"/>' +
      '<path d="M12 10.3c0-3.2.9-5.1 2.7-5.1 1.6 0 2.3 1.8 1.2 3.2-.9 1.2-2.3 1.8-3.9 1.9z" transform="rotate(240 12 12)"/>',
    ac:
      '<rect x="3" y="5.5" width="18" height="7.5" rx="1.6"/>' +
      '<path d="M6 10.2h12"/>' +
      '<path d="M8 16c1.3 0 1.3 2.2 2.6 2.2M14 16c1.3 0 1.3 2.2 2.6 2.2"/>',
    bulb:
      '<path d="M12 3.2a5.6 5.6 0 0 0-3.3 10.1c.5.4.8 1 .8 1.6v.4h5v-.4c0-.6.3-1.2.8-1.6A5.6 5.6 0 0 0 12 3.2z"/>' +
      '<path d="M9.8 18h4.4M10.6 20.6h2.8"/>',
    fridge:
      '<rect x="6" y="2.6" width="12" height="18.8" rx="1.6"/>' +
      '<path d="M6 9.4h12M9 5.2v2.1M9 11.6v2.6"/>',
    geyser:
      '<rect x="6.5" y="2.8" width="11" height="12.4" rx="3"/>' +
      '<path d="M9.5 15.2v1.6M14.5 15.2v1.6"/>' +
      '<path d="M12 21.4c1 0 1.8-.8 1.8-1.8S12 16.9 12 16.9s-1.8 1.7-1.8 2.7.8 1.8 1.8 1.8z"/>',
    hob:
      '<rect x="3" y="5" width="18" height="14" rx="1.6"/>' +
      '<circle cx="9" cy="11" r="3.2"/><path d="M16.5 9h2M16.5 12h2"/>',
    microwave:
      '<rect x="2.5" y="5.5" width="19" height="13" rx="1.6"/>' +
      '<rect x="5" y="8" width="9.5" height="8" rx="1"/>' +
      '<path d="M17.5 9v1.5M17.5 13v1.5"/>',
    kettle:
      '<path d="M6.5 9.5h8.8a3.7 3.7 0 0 1 3.7 3.7v.9a3.7 3.7 0 0 1-3.7 3.7H8.9a3.7 3.7 0 0 1-3.7-3.7v-3.3"/>' +
      '<path d="M5.2 10.6 3 8.2M9 9.5V7.4h4V9.5"/><path d="M7.5 20.6h9"/>',
    tv:
      '<rect x="2.5" y="4.5" width="19" height="12.5" rx="1.6"/>' +
      '<path d="M9 20.4h6M12 17v3.4"/>',
    router:
      '<rect x="3" y="13" width="18" height="6" rx="1.6"/>' +
      '<path d="M7 16h.01M10 16h.01"/>' +
      '<path d="M16.5 10.5a4 4 0 0 0-4-4M19.5 10.5a7 7 0 0 0-7-7"/>',
    pump:
      '<circle cx="11" cy="13" r="5.5"/><path d="M11 13V8.2M11 8.2h4.4"/>' +
      '<path d="M16.5 6.4h3.8v3.6M4 20.4h14"/>',
    washer:
      '<rect x="4.5" y="2.8" width="15" height="18.4" rx="1.8"/>' +
      '<circle cx="12" cy="14" r="4.2"/><path d="M7.5 6.4h.01M10.5 6.4h.01"/>',
    iron:
      '<path d="M3 16.5c0-4.1 3.6-7.4 8-7.4h10v3.2a4.2 4.2 0 0 1-4.2 4.2z"/>' +
      '<path d="M3 19.6h18M8.5 9.1V6.4h7.5"/>',
    plug:
      '<path d="M9 3.4v5M15 3.4v5"/>' +
      '<path d="M5.5 8.4h13v3.1a6.5 6.5 0 0 1-6.5 6.5 6.5 6.5 0 0 1-6.5-6.5z"/>' +
      '<path d="M12 18v3"/>'
  };

  var ICON_BY_KEY = {
    fan_regular: 'fan', fan_bldc: 'fan', fan_table: 'fan', fan_exhaust: 'fan',
    ac_noninv_1_0: 'ac', ac_inv_1_0: 'ac',
    ac_noninv_1_5: 'ac', ac_inv_1_5: 'ac', air_cooler: 'ac',
    led_9: 'bulb', led_18: 'bulb', tube_light: 'bulb',
    cfl_20: 'bulb', incand_60: 'bulb',
    fridge: 'fridge', deep_freezer: 'fridge',
    fridge_star: 'fridge', freezer_star: 'fridge',
    geyser: 'geyser', instant_geyser: 'geyser',
    induction: 'hob', rice_cooker: 'hob', oven: 'hob', blender: 'hob',
    microwave: 'microwave', kettle: 'kettle',
    tv_led: 'tv', tv_crt: 'tv', desktop_pc: 'tv',
    router: 'router', pump_1hp: 'pump', pump_05hp: 'pump',
    washer: 'washer', iron: 'iron',
    fan_pedestal: 'fan', fan_wall: 'fan', fan_rechargeable: 'fan',
    led_strip: 'bulb', lamp_table: 'bulb', light_emergency: 'bulb',
    led_panel: 'bulb', led_flood: 'bulb', light_lantern: 'bulb',
    otg: 'hob', air_fryer: 'hob', toaster: 'hob',
    pressure_cooker_electric: 'hob', sandwich_maker: 'hob',
    food_processor: 'hob', slow_cooker: 'hob',
    water_purifier: 'pump', dishwasher: 'washer',
    juicer: 'kettle', hand_mixer: 'kettle', coffee_maker: 'kettle',
    laptop: 'tv', monitor: 'tv', gaming_console: 'tv',
    set_top_box: 'router', projector: 'tv', soundbar: 'router',
    pump_submersible: 'pump', water_heater_rod: 'geyser',
    hair_dryer: 'iron', hair_straightener: 'iron', shaver_electric: 'iron',
    toothbrush_electric: 'iron', hair_clipper: 'iron', massager: 'iron',
    hair_curler: 'iron',
    vacuum_cleaner: 'plug', cctv_nvr: 'router', air_purifier: 'ac',
    sewing_machine: 'iron', room_heater: 'ac'
  };

  var ICON_BY_GROUP = {
    cooling: 'fan', lighting: 'bulb', kitchen: 'hob',
    entertainment: 'tv', water: 'pump', personal: 'plug', other: 'plug'
  };

  var COUNT_UP_MS = 420;
  var ANNOUNCE_MS = 900;
  // Long enough to actually be read (DESIGN.md §1.7 asks for >= 5s), and
  // dismissible before that either way.
  var TOAST_MS = 7000;
  // How many live search results to render at once — the catalog runs to
  // ~75 entries and a query like "e" would otherwise dump most of it.
  var SEARCH_LIMIT = 8;

  var els = {};
  var lines = {}; // line id -> {root, key, watts, wattsError, qty, hours, units}
  var groups = {}; // appliance key -> {root, subtotal}
  // null = no baseline yet. Set on first recalc and on entering Step 2 so the
  // user is never greeted by a warning about a tier the preset already crossed.
  var lastSlabIndex = null;
  var displayedTotal = 0;
  var countUpRaf = null;
  var announceTimer = null;
  var activeToast = null;
  var toastTimer = null;
  var customSeq = 0;
  var lineSeq = 0;
  var activeTabKey = null;
  var customFormEl = null;
  var booted = false;

  // No family-size question is asked any more; every first visit starts from
  // this one preset (Step 2 is fully editable regardless).
  var DEFAULT_PRESET = 'medium';

  /* ------------------------------------------------------------- helpers */

  function $(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function $$(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function W() {
    return root.EC_WIZARD;
  }

  function state() {
    return root.EC_WIZARD.state;
  }

  function i18n() {
    return root.EC_I18N;
  }

  /** The appliance's display name in the active locale. */
  function label(def) {
    return i18n().pick(def);
  }

  function catLabel(cat) {
    return i18n().t(cat.i18nKey);
  }

  function catLabelForGroup(groupKey) {
    var cat = CATEGORIES.filter(function (c) { return c.key === groupKey; })[0];
    return cat ? catLabel(cat) : '';
  }

  function icon(def) {
    var name = ICON_BY_KEY[def.key] || ICON_BY_GROUP[def.group] || 'plug';
    return (
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" ' +
      'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" ' +
      'stroke-linejoin="round">' + (ICONS[name] || ICONS.plug) + '</svg>'
    );
  }

  /** International digits with thousands separators, per the copy rules. */
  function fmtTaka(n) {
    return Math.round(n).toLocaleString('en-US');
  }

  function fmtUnits(n) {
    return (Math.round(n * 10) / 10).toLocaleString('en-US');
  }

  function reducedMotion() {
    return root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ------------------------------------------------------------ line ops */

  function linesForKey(key) {
    return state().appliances.filter(function (s) { return s.key === key; });
  }

  function hasKey(key) {
    return linesForKey(key).length > 0;
  }

  function findLine(id) {
    var list = state().appliances;
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === id) return list[i];
    }
    return null;
  }

  function nextLineId() {
    lineSeq++;
    return 'L' + lineSeq;
  }

  /**
   * Add a NEW independent line for `def`. Unlike the old single-selection
   * model, this never checks whether the appliance is already in the list —
   * that is exactly what lets the same appliance appear more than once, at
   * a different wattage each time ("+ different wattage").
   */
  function addLine(def, opts) {
    opts = opts || {};
    var sel = {
      id: nextLineId(),
      key: def.key,
      qty: opts.qty != null ? opts.qty : 1,
      hours: opts.hours != null ? opts.hours : defaultHours(def),
      watts: opts.watts != null ? opts.watts : def.watts
    };
    state().appliances.push(sel);
    return sel;
  }

  function removeLine(id) {
    var list = state().appliances;
    for (var i = list.length - 1; i >= 0; i--) {
      if (list[i].id === id) {
        list.splice(i, 1);
        break;
      }
    }
  }

  function removeGroup(key) {
    var list = state().appliances;
    for (var i = list.length - 1; i >= 0; i--) {
      if (list[i].key === key) list.splice(i, 1);
    }
  }

  /**
   * Backfill `id` on any selection restored from an older localStorage
   * payload (before this run), and fast-forward the id/custom sequences past
   * whatever is already in the list — the same defensive pattern the old
   * `customSeq` scan used, just covering both counters now.
   */
  function ensureLineIds() {
    state().appliances.forEach(function (sel) {
      if (!sel.id) {
        sel.id = nextLineId();
      } else {
        var m = /^L(\d+)$/.exec(String(sel.id));
        if (m) {
          var n = parseInt(m[1], 10);
          if (isFinite(n) && n > lineSeq) lineSeq = n;
        }
      }
      if (sel.custom) {
        var cm = /^custom_(\d+)$/.exec(String(sel.key));
        if (cm) {
          var cn = parseInt(cm[1], 10);
          if (isFinite(cn) && cn > customSeq) customSeq = cn;
        }
      }
    });
  }

  /** Unique appliance keys in the order they first appear — the group order. */
  function groupedKeys() {
    var seen = {};
    var order = [];
    state().appliances.forEach(function (sel) {
      if (!seen[sel.key]) {
        seen[sel.key] = true;
        order.push(sel.key);
      }
    });
    return order;
  }

  /** Sensible starting hours so a newly-added line is never obviously wrong. */
  function defaultHours(def) {
    if (def.key === 'fridge' || def.key === 'deep_freezer') return 24;
    if (def.group === 'lighting') return 6;
    if (def.group === 'cooling') return def.key.indexOf('ac_') === 0 ? 8 : 12;
    if (def.group === 'entertainment') return def.key === 'router' ? 24 : 5;
    if (def.group === 'kitchen') return 1;
    if (def.group === 'water') return 1;
    return 1;
  }

  function markTouched() {
    if (!state().touchedAppliances) state().touchedAppliances = true;
    W().save();
  }

  /* ------------------------------------------------------------- picker */

  function buildTabs() {
    var container = els.tabs;
    if (!container) return;
    container.innerHTML = '';
    if (!activeTabKey) activeTabKey = CATEGORIES[0].key;

    CATEGORIES.forEach(function (cat) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ec-tab';
      btn.setAttribute('data-ec-tab', cat.key);
      btn.setAttribute('aria-pressed', cat.key === activeTabKey ? 'true' : 'false');
      btn.textContent = catLabel(cat);
      btn.addEventListener('click', function () { setActiveTab(cat.key); });
      container.appendChild(btn);
    });
  }

  function setActiveTab(key) {
    activeTabKey = key;
    $$('[data-ec-tab]', els.tabs).forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-ec-tab') === key ? 'true' : 'false');
    });
    renderChips();
  }

  /** The active tab's FULL category list as tappable "+" chips. */
  function renderChips() {
    var container = els.chips;
    if (!container) return;
    container.innerHTML = '';

    var defs = (root.EC_RATES.appliances || []).filter(function (d) { return d.group === activeTabKey; });
    defs.forEach(function (def) {
      var added = hasKey(def.key);
      var name = label(def);

      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'ec-chip';
      chip.disabled = added;
      chip.setAttribute('data-ec-chip', def.key);
      chip.setAttribute(
        'aria-label',
        i18n().t(added ? 'step2.add.addedAriaLabel' : 'step2.add.ariaLabel', { name: name, watts: def.watts })
      );
      chip.innerHTML =
        '<span class="ec-chip__name">' + name + '</span>' +
        '<span class="ec-chip__watts ec-num">' + def.watts + 'W</span>';

      chip.addEventListener('click', function () {
        if (chip.disabled) return;
        addLine(def);
        markTouched();
        renderChips();
        renderList();
        recalc();
        // Removed focusGroupFirstInput so users can add multiple items before scrolling down.
      });

      container.appendChild(chip);
    });
  }

  /** Re-paint just the parts of the picker whose disabled state can drift. */
  function refreshPicker() {
    renderChips();
    if (els.search && els.search.value) renderSearchResults(els.search.value);
  }

  function renderSearchResults(query) {
    var container = els.searchResults;
    if (!container) return;
    container.innerHTML = '';

    var q = (query || '').trim().toLowerCase();
    if (!q) {
      container.hidden = true;
      return;
    }
    container.hidden = false;

    var matches = (root.EC_RATES.appliances || [])
      .filter(function (def) { return label(def).toLowerCase().indexOf(q) !== -1; })
      .slice(0, SEARCH_LIMIT);

    if (!matches.length) {
      var empty = document.createElement('li');
      empty.className = 'ec-picker__empty';
      empty.textContent = i18n().t('step2.search.empty');
      container.appendChild(empty);

      var addCustomLi = document.createElement('li');
      var addCustomBtn = document.createElement('button');
      addCustomBtn.type = 'button';
      addCustomBtn.className = 'ec-linkbtn';
      addCustomBtn.textContent = i18n().t('custom.open');
      addCustomBtn.addEventListener('click', function () { openCustomForm(query); });
      addCustomLi.appendChild(addCustomBtn);
      container.appendChild(addCustomLi);
      return;
    }

    matches.forEach(function (def) {
      var added = hasKey(def.key);
      var name = label(def);

      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ec-picker__result';
      btn.disabled = added;
      btn.setAttribute(
        'aria-label',
        i18n().t(added ? 'step2.add.addedAriaLabel' : 'step2.add.ariaLabel', { name: name, watts: def.watts })
      );
      btn.innerHTML =
        '<span class="ec-picker__resultname">' + name + '</span>' +
        '<span class="ec-picker__resultmeta">' +
        '<span class="ec-picker__resultwatts ec-num">' + def.watts + 'W</span>' +
        '<span class="ec-picker__resultcat">' + catLabelForGroup(def.group) + '</span>' +
        '</span>';

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        addLine(def);
        markTouched();
        if (els.search) els.search.value = '';
        container.hidden = true;
        renderChips();
        renderList();
        recalc();
      });

      li.appendChild(btn);
      container.appendChild(li);
    });
  }

  /* ------------------------------------------------------ custom appliance */

  /**
   * The "+ Add a device that's not in the list" form (unchanged from the
   * previous run's Run 4 Phase 3a flow, just re-homed: it now lives once
   * under the picker rather than once per category, and search's "no match"
   * state is the way in rather than a per-category "add more" panel).
   */
  function buildCustomForm() {
    var t = i18n().t;
    var wrap = document.createElement('div');
    wrap.className = 'ec-custom';

    var catOptions = CATEGORIES.map(function (c) {
      return '<option value="' + c.key + '">' + catLabel(c) + '</option>';
    }).join('');

    wrap.innerHTML =
      '<p class="ec-custom__title">' + t('custom.title') + '</p>' +
      '<div class="ec-custom__fields">' +
      '<div class="ec-custom__f"><label class="ec-stepper__label" for="ec-custom-name">' + t('custom.name.label') + '</label>' +
      '<input class="ec-input ec-input--text" id="ec-custom-name" type="text" maxlength="40" data-custom-name></div>' +
      '<div class="ec-custom__f"><label class="ec-stepper__label" for="ec-custom-cat">' + t('custom.category.label') + '</label>' +
      '<span class="ec-field__sel"><select class="ec-select" id="ec-custom-cat" data-custom-category>' + catOptions + '</select></span></div>' +
      '<div class="ec-custom__f"><label class="ec-stepper__label" for="ec-custom-watts">' + t('custom.watts.label') + '</label>' +
      '<input class="ec-input" id="ec-custom-watts" type="number" inputmode="numeric" min="1" max="10000" step="1" data-custom-watts></div>' +
      '<div class="ec-custom__f"><label class="ec-stepper__label" for="ec-custom-qty">' + t('custom.qty.label') + '</label>' +
      '<input class="ec-input" id="ec-custom-qty" type="number" inputmode="numeric" min="1" max="99" step="1" value="1" data-custom-qty></div>' +
      '<div class="ec-custom__f"><label class="ec-stepper__label" for="ec-custom-hours">' + t('custom.hours.label') + '</label>' +
      '<input class="ec-input" id="ec-custom-hours" type="number" inputmode="decimal" min="0" max="24" step="0.5" value="1" data-custom-hours></div>' +
      '</div>' +
      '<p class="ec-field__error" data-custom-error hidden></p>' +
      '<button class="ec-btn ec-btn--ghost ec-btn--sm" type="button" data-custom-add>' + t('custom.add') + '</button>';

    var err = $('[data-custom-error]', wrap);
    var nameInput = $('[data-custom-name]', wrap);
    var catSelect = $('[data-custom-category]', wrap);
    var wattsInput = $('[data-custom-watts]', wrap);
    var qtyInput = $('[data-custom-qty]', wrap);
    var hoursInput = $('[data-custom-hours]', wrap);

    $('[data-custom-add]', wrap).addEventListener('click', function () {
      var name = nameInput.value.trim();
      var targetGroup = catSelect.value;
      var watts = parseFloat(wattsInput.value);
      var validWatts = isFinite(watts) && watts >= root.EC.WATTS_MIN && watts <= root.EC.WATTS_MAX;
      var q = parseInt(qtyInput.value, 10);
      var h = parseFloat(hoursInput.value);

      if (!name) {
        err.textContent = t('custom.error');
        err.hidden = false;
        nameInput.focus();
        return;
      }
      if (!validWatts) {
        err.textContent = t('custom.wattsRangeError');
        err.hidden = false;
        wattsInput.focus();
        return;
      }
      err.hidden = true;

      customSeq++;
      // See CATEGORY_FALLBACK_RECOMMEND in ec-rates-config.js — only cooling
      // and lighting get one; every other category leaves this null and the
      // appliance simply carries no recommendation, on purpose.
      var fallback = (root.EC_RATES.categoryFallbackRecommend || {})[targetGroup] || null;
      var key = 'custom_' + customSeq;

      // Session-only, like every other selection: nothing here is ever sent
      // to Shopify, it lives in EC_WIZARD.state and localStorage exactly the
      // way a catalog pick does.
      state().appliances.push({
        id: nextLineId(),
        key: key,
        custom: true,
        label: name,
        group: targetGroup,
        watts: watts,
        qty: isFinite(q) && q > 0 ? q : 1,
        hours: isFinite(h) && h >= 0 ? h : 1,
        recommend: fallback ? fallback.tag : null,
        recommendTo: fallback ? fallback.recommendTo : null,
        store: fallback ? fallback.store : null
      });

      nameInput.value = '';
      wattsInput.value = '';
      qtyInput.value = '1';
      hoursInput.value = '1';

      markTouched();
      closeCustomForm();
      if (els.search) els.search.value = '';
      if (els.searchResults) els.searchResults.hidden = true;
      renderChips();
      renderList();
      recalc();
      focusGroupFirstInput(key, false);
    });

    return wrap;
  }

  function ensureCustomForm() {
    if (!customFormEl) {
      customFormEl = buildCustomForm();
      if (els.customHolder) els.customHolder.appendChild(customFormEl);
    }
    return customFormEl;
  }

  function openCustomForm(prefillName) {
    ensureCustomForm();
    if (els.customHolder) els.customHolder.hidden = false;
    var nameInput = $('[data-custom-name]', customFormEl);
    if (nameInput) {
      if (prefillName) nameInput.value = prefillName.trim();
      nameInput.focus();
    }
  }

  function closeCustomForm() {
    if (els.customHolder) els.customHolder.hidden = true;
  }

  /* --------------------------------------------------------- grouped list */

  function focusGroupFirstInput(key, useLast) {
    var groupLines = linesForKey(key);
    if (!groupLines.length) return;
    var target = useLast ? groupLines[groupLines.length - 1] : groupLines[0];
    var L = lines[target.id];
    if (!L || !L.watts) return;
    L.watts.focus();
    if (L.root.scrollIntoView) {
      L.root.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
    }
  }

  function refreshLineUnits(id) {
    var L = lines[id];
    var sel = findLine(id);
    if (!L || !sel) return;
    L.units.textContent = fmtUnits(root.EC.unitsForSelection(sel));
  }

  /** Snap qty/hours back to clamped state and refresh derived text. Not used
   * for watts — see the comment on commitWatts() below for why. */
  function syncLineValues(id) {
    var L = lines[id];
    var sel = findLine(id);
    if (!L || !sel) return;
    L.qty.value = sel.qty;
    L.hours.value = sel.hours;
    refreshLineUnits(id);
    updateGroupSubtotal(sel.key);
  }

  function updateGroupSubtotal(key) {
    var g = groups[key];
    if (!g) return;
    var total = linesForKey(key).reduce(function (sum, sel) {
      return sum + root.EC.unitsForSelection(sel);
    }, 0);
    g.subtotal.textContent = fmtUnits(total);
  }

  function buildLine(sel, def) {
    var tpl = $('[data-ec-line-template]');
    var node = tpl.content.firstElementChild.cloneNode(true);
    node.setAttribute('data-ec-line', sel.id);

    var wattsEl = $('[data-line-watts-input]', node);
    var wattsErr = $('[data-line-watts-error]', node);
    var qtyEl = $('[data-line-qty]', node);
    var hoursEl = $('[data-line-hours]', node);
    var unitsEl = $('[data-line-units]', node);
    var name = label(def);

    var wattsId = 'ec-l-watts-' + sel.id;
    var qtyId = 'ec-l-qty-' + sel.id;
    var hoursId = 'ec-l-hours-' + sel.id;
    wattsEl.id = wattsId;
    qtyEl.id = qtyId;
    hoursEl.id = hoursId;

    $('[data-line-watts-label]', node).textContent = i18n().t('tile.watts');
    $('[data-line-watts-label]', node).setAttribute('for', wattsId);
    $('[data-line-qty-label]', node).textContent = i18n().t('tile.qty');
    $('[data-line-qty-label]', node).setAttribute('for', qtyId);
    $('[data-line-hours-label]', node).textContent = i18n().t('tile.hours');
    $('[data-line-hours-label]', node).setAttribute('for', hoursId);
    $('[data-line-units-label]', node).textContent = i18n().t('tile.unitsPerMonth');
    wattsErr.textContent = i18n().t('tile.wattsError');
    wattsErr.id = wattsId + '-error';
    wattsEl.setAttribute('aria-describedby', wattsErr.id);

    $('[data-line-qty-dec-label]', node).textContent = i18n().t('tile.qtyDec', { name: name });
    $('[data-line-qty-inc-label]', node).textContent = i18n().t('tile.qtyInc', { name: name });
    $('[data-line-hours-dec-label]', node).textContent = i18n().t('tile.hoursDec', { name: name });
    $('[data-line-hours-inc-label]', node).textContent = i18n().t('tile.hoursInc', { name: name });

    var removeBtn = $('[data-line-remove]', node);
    removeBtn.innerHTML = '<span aria-hidden="true">&times;</span><span class="ec-vh">' +
      i18n().t('step2.line.removeAria', { name: name }) + '</span>';

    wattsEl.value = root.EC.resolveDef(sel).watts;
    qtyEl.value = sel.qty;
    hoursEl.value = sel.hours;
    unitsEl.textContent = fmtUnits(root.EC.unitsForSelection(sel));

    function commit() {
      var live = findLine(sel.id);
      if (!live) return;
      var q = parseInt(qtyEl.value, 10);
      var h = parseFloat(hoursEl.value);
      live.qty = isFinite(q) && q > 0 ? Math.min(q, 99) : 1;
      live.hours = isFinite(h) && h >= 0 ? Math.min(h, 24) : 0;
      markTouched();
      syncLineValues(sel.id);
      recalc();
    }

    qtyEl.addEventListener('input', commit);
    hoursEl.addEventListener('input', commit);

    /**
     * Wattage: unlike qty/hours, an out-of-range value gets a visible inline
     * error rather than a silent clamp — but the STATE never holds a broken
     * number either way. While the typed value is out of [1, 10000] or
     * unparseable, the line's stored watts simply keeps its last valid value
     * (the calc reads that, unaffected) and the error shows; the field
     * itself is left showing exactly what the user typed, since rewriting it
     * out from under them mid-keystroke would fight their typing. The
     * field's value is deliberately NOT reset here for that same reason —
     * only on blur do we snap it back to the resolved value.
     */
    function commitWatts() {
      var live = findLine(sel.id);
      if (!live) return;
      var v = parseFloat(wattsEl.value);
      var valid = isFinite(v) && v >= root.EC.WATTS_MIN && v <= root.EC.WATTS_MAX;
      if (valid) {
        live.watts = v;
        wattsEl.setAttribute('aria-invalid', 'false');
        wattsErr.hidden = true;
      } else {
        wattsEl.setAttribute('aria-invalid', 'true');
        wattsErr.hidden = false;
      }
      markTouched();
      refreshLineUnits(sel.id);
      updateGroupSubtotal(live.key);
      recalc();
    }

    wattsEl.addEventListener('input', commitWatts);
    wattsEl.addEventListener('blur', function () {
      var live = findLine(sel.id);
      if (!live) return;
      wattsEl.value = root.EC.resolveDef(live).watts;
      wattsEl.setAttribute('aria-invalid', 'false');
      wattsErr.hidden = true;
    });

    function bindAnimatedStepper(input, decBtn, incBtn, track, delta, min, max) {
      if (!track || !decBtn || !incBtn) return;
      var currentVal = parseFloat(input.value) || min;
      
      function renderNum(val, dir) {
        var nodes = track.querySelectorAll('.ec-anim-stepper__num');
        var old = nodes[nodes.length - 1];
        
        for (var i = 0; i < nodes.length - 1; i++) {
          if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
        }
        
        var span = document.createElement('div');
        span.className = 'ec-anim-stepper__num';
        span.textContent = val;
        
        if (dir !== 0) {
          span.style.transform = 'translateX(' + (dir * 100) + '%)';
          span.style.opacity = '0';
          if (old) {
            old.style.transform = 'translateX(' + (-dir * 100) + '%)';
            old.style.opacity = '0';
            setTimeout(function() { if (old.parentNode) old.parentNode.removeChild(old); }, 300);
          }
        }
        
        track.appendChild(span);
        
        if (dir !== 0) {
          span.offsetHeight;
          span.style.transform = 'translateX(0)';
          span.style.opacity = '1';
        }
      }
      
      renderNum(currentVal, 0);
      
      decBtn.addEventListener('click', function() {
        if (currentVal > min) {
          currentVal = Math.max(min, currentVal - delta);
          renderNum(currentVal, -1);
          input.value = currentVal;
          commit();
        }
      });
      
      incBtn.addEventListener('click', function() {
        if (currentVal < max) {
          currentVal = Math.min(max, currentVal + delta);
          renderNum(currentVal, 1);
          input.value = currentVal;
          commit();
        }
      });
      
      input.addEventListener('input', function() {
        var v = parseFloat(input.value);
        if (isFinite(v) && v !== currentVal) {
          var dir = v > currentVal ? 1 : -1;
          currentVal = v;
          renderNum(v, dir);
        }
      });
    }

    bindAnimatedStepper(qtyEl, $('[data-line-qty-dec]', node), $('[data-line-qty-inc]', node), $('[data-line-qty-track]', node), 1, 1, 99);
    bindAnimatedStepper(hoursEl, $('[data-line-hours-dec]', node), $('[data-line-hours-inc]', node), $('[data-line-hours-track]', node), 0.5, 0, 24);

    removeBtn.addEventListener('click', function () {
      removeLine(sel.id);
      markTouched();
      renderList();
      refreshPicker();
      recalc();
    });

    lines[sel.id] = {
      root: node,
      key: sel.key,
      watts: wattsEl,
      wattsError: wattsErr,
      qty: qtyEl,
      hours: hoursEl,
      units: unitsEl
    };

    return node;
  }

  function buildGroup(key) {
    var groupLines = linesForKey(key);
    var def = root.EC.resolveDef(groupLines[0]);
    var name = label(def);

    var wrap = document.createElement('div');
    wrap.className = 'ec-group';
    wrap.setAttribute('data-ec-group', key);

    var head = document.createElement('div');
    head.className = 'ec-group__head';
    head.innerHTML =
      '<span class="ec-group__icon" aria-hidden="true">' + icon(def) + '</span>' +
      '<span class="ec-group__name">' + name + '</span>' +
      '<span class="ec-group__subtotal">' +
      '<span class="ec-num" data-group-subtotal>0</span> ' +
      '<span class="ec-group__subtotal-label">' + i18n().t('tile.unitsPerMonth') + '</span>' +
      '</span>';

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'ec-group__remove';
    removeBtn.innerHTML = '<span aria-hidden="true">&times;</span><span class="ec-vh">' +
      i18n().t('step2.group.removeAria', { name: name }) + '</span>';
    removeBtn.addEventListener('click', function () {
      removeGroup(key);
      markTouched();
      renderList();
      refreshPicker();
      recalc();
    });
    head.appendChild(removeBtn);
    wrap.appendChild(head);

    var linesWrap = document.createElement('div');
    linesWrap.className = 'ec-group__lines';
    groupLines.forEach(function (sel) {
      linesWrap.appendChild(buildLine(sel, def));
    });
    wrap.appendChild(linesWrap);

    var foot = document.createElement('div');
    foot.className = 'ec-group__foot';
    var addWattageBtn = document.createElement('button');
    addWattageBtn.type = 'button';
    addWattageBtn.className = 'ec-linkbtn ec-linkbtn--quiet';
    addWattageBtn.textContent = i18n().t('step2.group.addWattage');
    addWattageBtn.addEventListener('click', function () {
      // Every new line starts from the CATALOG default (not whatever the
      // last line was edited to) — the point is a genuinely different,
      // independently-editable wattage, not a copy of the previous one.
      var catalogDef = root.EC.getAppliance(key) || def;
      addLine(catalogDef);
      markTouched();
      renderList();
      refreshPicker();
      recalc();
      focusGroupFirstInput(key, true);
    });
    foot.appendChild(addWattageBtn);
    wrap.appendChild(foot);

    groups[key] = { root: wrap, subtotal: $('[data-group-subtotal]', head) };
    updateGroupSubtotal(key);

    return wrap;
  }

  function renderList() {
    var container = els.list;
    if (!container) return;
    container.innerHTML = '';
    lines = {};
    groups = {};

    var keys = groupedKeys();
    if (!keys.length) {
      container.hidden = true;
      if (els.empty) els.empty.hidden = false;
      return;
    }

    container.hidden = false;
    if (els.empty) els.empty.hidden = true;
    keys.forEach(function (key) {
      container.appendChild(buildGroup(key));
    });
  }

  function renderAll() {
    buildTabs();
    renderChips();
    renderList();
    if (els.search) els.search.value = '';
    if (els.searchResults) els.searchResults.hidden = true;
  }

  /* ------------------------------------------------------------- presets */

  function applyPreset(size) {
    var preset = root.EC_RATES.familyPresets[size] || [];
    state().appliances = preset.map(function (p) {
      var def = root.EC.getAppliance(p.key);
      return { id: nextLineId(), key: p.key, qty: p.qty, hours: p.hours, watts: def ? def.watts : undefined };
    });
    state().touchedAppliances = false;
    W().save();
    renderAll();
    recalc();
  }

  /* ---------------------------------------------------------- live meter */

  function currentBill() {
    var units = root.EC.totalUnits(state().appliances);
    return { units: units, bill: root.EC.calcBill(W().getBillInputs(units)) };
  }

  function countUp(to) {
    if (countUpRaf) cancelAnimationFrame(countUpRaf);
    var from = displayedTotal;
    // requestAnimationFrame does not run in a background tab, so animating there
    // would leave the readout frozen at its old value — a page opened in a
    // background tab would show ৳0. Write straight through instead.
    var hidden = typeof document !== 'undefined' && document.hidden;
    if (reducedMotion() || hidden || Math.abs(to - from) < 1) {
      displayedTotal = to;
      if (els.total) els.total.textContent = fmtTaka(to);
      return;
    }
    var start = performance.now();
    function frame(now) {
      var p = Math.min(1, (now - start) / COUNT_UP_MS);
      // easeOutCubic — decelerating feels like a readout settling
      var eased = 1 - Math.pow(1 - p, 3);
      var v = from + (to - from) * eased;
      if (els.total) els.total.textContent = fmtTaka(v);
      if (p < 1) {
        countUpRaf = requestAnimationFrame(frame);
      } else {
        displayedTotal = to;
        countUpRaf = null;
      }
    }
    countUpRaf = requestAnimationFrame(frame);
  }

  function renderLadder(energy) {
    if (!els.ladder) return;
    els.ladder.innerHTML = '';
    var total = energy.breakdown.reduce(function (a, r) { return a + r.units; }, 0);
    if (total <= 0) return;

    energy.breakdown.forEach(function (row) {
      // row.slabIndex comes from the math layer. Matching on rate would break
      // the moment two tiers happen to share a rate.
      var seg = document.createElement('span');
      seg.className = 'ec-meter__seg';
      seg.style.width = (row.units / total) * 100 + '%';
      seg.style.background = row.isLifeline
        ? 'var(--ec-slab-1)'
        : 'var(--ec-slab-' + Math.min(6, row.slabIndex + 1) + ')';
      els.ladder.appendChild(seg);
    });
  }

  function setPulse(units) {
    if (!els.pulse) return;
    // A real prepaid meter's LED pulses in proportion to draw. Faster = costlier.
    var dur = Math.max(0.34, Math.min(2.4, 2.4 - units / 320));
    els.pulse.style.setProperty('--ec-pulse-dur', dur.toFixed(2) + 's');
    els.pulse.setAttribute('data-live', units > 0 ? 'true' : 'false');
  }

  /**
   * Drive the device's severity state — sage LCD / rising flag / inverted LCD.
   *
   * The state name comes from EC.severityFor() so the Step-3 bill shows exactly
   * the same thing. Everything visual hangs off the data attribute in CSS; this
   * function only writes the state and the flag's words.
   */
  function setSeverity(bill) {
    if (!els.meterBar) return;
    var cat = root.EC.getCategory(state().company, state().category);
    var sev = root.EC.severityFor(bill.marginalIndex, cat);
    els.meterBar.setAttribute('data-ec-severity', sev);

    if (!els.flagText) return;
    // The flag says which tier, not just that something changed — a bare
    // "slab climbing" leaves the user with nothing to act on.
    var rate = bill.marginalRate.toFixed(2);
    els.flagText.textContent = sev === 'danger'
      ? i18n().t('meter.flag.danger', { rate: rate })
      : i18n().t('meter.flag.warn', { rate: rate });
  }

  function announce(bill, units) {
    if (!els.announce) return;
    // Debounced: putting aria-live on the animating figure would narrate every
    // frame of the count-up.
    if (announceTimer) clearTimeout(announceTimer);
    announceTimer = setTimeout(function () {
      els.announce.textContent = i18n().t('meter.announce', {
        total: fmtTaka(bill.grandTotal),
        units: fmtUnits(units)
      });
    }, ANNOUNCE_MS);
  }

  /** The alert is reserved for the expensive tiers — see the config comment. */
  function alertFromIndex() {
    var v = root.EC_RATES.common.slabAlertFromIndex;
    return typeof v === 'number' ? v : 0;
  }

  function checkSlabCrossing(bill) {
    var idx = typeof bill.marginalIndex === 'number' ? bill.marginalIndex : -1;

    // No baseline yet (first paint, or arriving on Step 2): record and stay quiet.
    if (lastSlabIndex === null) {
      lastSlabIndex = idx;
      return;
    }

    // Fire only when moving UP, and only into a tier worth warning about.
    // Crossing ৳6.18 -> ৳8.50 is not an "aha", it is noise that teaches the user
    // to dismiss the toast that actually matters.
    if (idx > lastSlabIndex && idx >= alertFromIndex()) {
      toast(i18n().t('meter.toast.warn', { rate: bill.marginalRate.toFixed(2) }), 'warn');
    }
    lastSlabIndex = idx;
  }

  function dismissToast() {
    if (toastTimer) {
      clearTimeout(toastTimer);
      toastTimer = null;
    }
    if (activeToast && activeToast.parentNode) activeToast.remove();
    activeToast = null;
  }

  /**
   * Show a single toast, replacing any current one.
   *
   * Crossings can arrive in quick succession — holding the quantity stepper
   * walks the user through several tiers — and a stack of three warnings covers
   * the grid they are trying to use. Only the newest crossing is still true, so
   * it replaces its predecessor rather than queueing behind it.
   */
  function toast(message, tone) {
    if (!els.toasts) return;
    dismissToast();

    var node = document.createElement('div');
    node.className = 'ec-toast' + (tone ? ' ec-toast--' + tone : '');

    var text = document.createElement('span');
    text.className = 'ec-toast__text';
    text.textContent = message;

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'ec-toast__close';
    close.innerHTML = '<span aria-hidden="true">&times;</span><span class="ec-vh">' + i18n().t('meter.toast.close') + '</span>';
    close.addEventListener('click', function () {
      if (activeToast === node) dismissToast();
      else if (node.parentNode) node.remove();
    });

    node.appendChild(text);
    node.appendChild(close);
    // The container is the live region (declared in the markup), so replacing
    // its contents is what gets announced.
    els.toasts.appendChild(node);

    activeToast = node;
    toastTimer = setTimeout(dismissToast, TOAST_MS);
  }

  /**
   * Run 4 Phase 4: EC.calcBill() throws for any category that isn't
   * 'LT-A' (there is no rate table for it to read) — this is checked BEFORE
   * currentBill() ever calls it, so selecting e.g. LT-B never throws into the
   * page. The live readout swaps for the "coming soon" notice instead; each
   * line's own unit count is unaffected, since a line's consumption has
   * nothing to do with which tariff bills it.
   */
  function showMeterComingSoon() {
    if (els.meterNotice) els.meterNotice.hidden = false;
    els.meterLive.forEach(function (el) { el.hidden = true; });
    if (els.meterBar) els.meterBar.setAttribute('data-ec-severity', 'normal');
  }

  function hideMeterComingSoon() {
    if (els.meterNotice) els.meterNotice.hidden = true;
    els.meterLive.forEach(function (el) { el.hidden = false; });
  }

  function recalc() {
    if (!root.EC || !root.EC_WIZARD) return;

    if (W().categoryFunctional && !W().categoryFunctional()) {
      showMeterComingSoon();
      Object.keys(lines).forEach(refreshLineUnits);
      Object.keys(groups).forEach(updateGroupSubtotal);
      W()._emit('recalc', null);
      return null;
    }
    hideMeterComingSoon();

    var res = currentBill();

    if (els.units) els.units.textContent = fmtUnits(res.units);
    countUp(res.bill.grandTotal);
    renderLadder(res.bill.energy);
    setPulse(res.units);
    setSeverity(res.bill);
    announce(res.bill, res.units);
    checkSlabCrossing(res.bill);

    Object.keys(lines).forEach(refreshLineUnits);
    Object.keys(groups).forEach(updateGroupSubtotal);

    // Tell the rest of the page. Appliance edits mutate the list in place and
    // save, so they never pass through setState() and never emit `change` —
    // which meant the always-visible Step 3 results kept a stale bill while the
    // pinned meter updated. A distinct event, because this module subscribes to
    // `change` itself and re-emitting that here would recurse.
    W()._emit('recalc', res);

    return res;
  }

  /* ----------------------------------------------------------------- boot */

  function init() {
    if (booted) return;
    els.list = $('[data-ec-appliance-list]');
    els.empty = $('[data-ec-appliance-empty]');
    els.search = $('[data-ec-search]');
    els.searchResults = $('[data-ec-search-results]');
    els.tabs = $('[data-ec-tabs]');
    els.chips = $('[data-ec-chips]');
    els.customHolder = $('[data-ec-custom-holder]');
    if (!els.list || !$('[data-ec-line-template]')) return;

    els.total = $('[data-ec-total]');
    els.units = $('[data-ec-units]');
    els.ladder = $('[data-ec-ladder]');
    els.pulse = $('[data-ec-pulse]');
    els.meterBar = $('[data-ec-meter-bar]');
    els.flagText = els.meterBar ? els.meterBar.querySelector('[data-ec-flag-text]') : null;
    els.meterNotice = $('[data-ec-meter-notice]');
    els.meterLive = els.meterBar ?
      Array.prototype.slice.call(els.meterBar.querySelectorAll('[data-ec-meter-live]')) : [];
    els.announce = $('[data-ec-announce]');
    els.toasts = $('[data-ec-toasts]');

    booted = true;
    ensureLineIds();

    if (els.search) {
      els.search.addEventListener('input', function () { renderSearchResults(els.search.value); });
    }

    if (els.tabs) {
      var isDown = false;
      var startX;
      var scrollLeft;
      els.tabs.addEventListener('mousedown', function(e) {
        isDown = true;
        els.tabs.style.cursor = 'grabbing';
        startX = e.pageX - els.tabs.offsetLeft;
        scrollLeft = els.tabs.scrollLeft;
      });
      els.tabs.addEventListener('mouseleave', function() {
        isDown = false;
        els.tabs.style.cursor = '';
      });
      els.tabs.addEventListener('mouseup', function() {
        isDown = false;
        els.tabs.style.cursor = '';
      });
      els.tabs.addEventListener('mousemove', function(e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - els.tabs.offsetLeft;
        var walk = (x - startX) * 2;
        els.tabs.scrollLeft = scrollLeft - walk;
      });
    }

    // First visit (or a cleared list) starts from the default preset, so the
    // user tweaks rather than building from an empty screen.
    if (!state().appliances.length && !state().touchedAppliances) {
      applyPreset(DEFAULT_PRESET);
    } else {
      renderAll();
      recalc();
    }

    W().on('change', function (e) {
      if (!e) return;
      // Load, meter type and late-payment changes all move the bill.
      if (e.reason === 'load' || e.reason === 'meter' || e.reason === 'late' ||
          e.reason === 'company' || e.reason === 'category') {
        recalc();
      }
    });

    // Coming back to a backgrounded tab: re-sync in case an animation was
    // suspended part-way through.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) recalc();
    });

    W().on('step', function (e) {
      // Entering Step 2 sets the baseline, so the user is not greeted by a slab
      // warning about a threshold the preset already crossed for them.
      if (e.to === 2) {
        var res = currentBill();
        lastSlabIndex = res.bill.marginalIndex;
      }
    });

    // Every group heading, chip, tab label and aria-label is JS-authored, so
    // toggling the language rebuilds the picker + list from state (selections
    // survive — renderAll()/renderList() read them straight back out of
    // state().appliances). The custom form, if it was ever opened, is rebuilt
    // fresh too rather than left showing stale-language labels.
    if (root.EC_I18N && root.EC_I18N.onChange) {
      root.EC_I18N.onChange(function () {
        if (customFormEl && customFormEl.parentNode) customFormEl.parentNode.removeChild(customFormEl);
        customFormEl = null;
        if (els.customHolder) els.customHolder.hidden = true;
        renderAll();
        recalc();
      });
    }
  }

  function boot() {
    if (root.EC_I18N && root.EC_I18N.whenReady) root.EC_I18N.whenReady(init);
    else init();
  }

  root.EC_APPLIANCES = {
    init: init,
    render: renderAll,
    recalc: recalc,
    applyPreset: applyPreset,
    toast: toast,
    CATEGORIES: CATEGORIES,
    _lines: function () { return lines; },
    _groups: function () { return groups; }
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
